import React, { useMemo, useState, useRef } from "react";
import { T, AWARD_COLORS, fmt } from "./theme";
import { SectionHeader, Card, Avatar, Progress, PilulaAwardTime, Btn } from "./ui";
import { calcPontosCorretor, calcAwardVitalicio } from "./awards";
import { AWARDS } from "./data";
import { supabase, isSupabaseReady } from "./supabase";

// ═══════════════════════════════════════════════════════════════════════
// MEU PERFIL
// Dados, badge, progresso, FOTO editável, link de indicação editável
// ═══════════════════════════════════════════════════════════════════════

const ORIGIN_INDICACAO = "https://app.bellanor.com.br/altaperformance?ref=";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9_-]/g, "")
    .slice(0, 30);
}

export default function MeuPerfil({ currentUser, corretores, times }) {
  const meu = useMemo(() =>
    corretores.find(c => c._uuid === currentUser._corretorUuid || c.id === currentUser.corretorId),
    [corretores, currentUser]
  );

  const [tagInput, setTagInput] = useState(meu?.tagUnica || meu?.codigoIndicacao || "");
  const [tagSalvando, setTagSalvando] = useState(false);
  const [tagMsg, setTagMsg] = useState(null);
  const [foto, setFoto] = useState(meu?.foto || meu?.foto_url || null);
  const [fotoUp, setFotoUp] = useState(false);
  const [fotoMsg, setFotoMsg] = useState(null);
  const fileRef = useRef(null);

  if (!meu) return <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Perfil não encontrado.</div>;

  const aw = calcAwardVitalicio(meu);
  const ac = AWARD_COLORS[aw.id] || {};
  const p = calcPontosCorretor(meu);
  const timeDoc = meu.timeId ? times?.find(t => t.id === meu.timeId) : null;
  const initials = meu.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  const awIdx = AWARDS.findIndex(a => a.id === aw.id);
  const nextAw = awIdx < AWARDS.length - 1 ? AWARDS[awIdx + 1] : null;
  const progresso = nextAw ? Math.min(100, Math.round((p.total / nextAw.pts) * 100)) : 100;

  const tagAtual = meu.tagUnica || meu.codigoIndicacao || "";
  const tagNormalizada = slugify(tagInput);
  const tagMudou = tagNormalizada !== (tagAtual || "").toLowerCase();
  const linkFinal = `${ORIGIN_INDICACAO}${tagAtual || tagNormalizada}`;

  async function salvarTag() {
    if (!isSupabaseReady || !meu?._uuid) return;
    const novo = slugify(tagInput);
    if (novo.length < 3) { setTagMsg({ tipo: "err", texto: "Mínimo 3 caracteres (a-z, 0-9, - e _)." }); return; }

    setTagSalvando(true);
    try {
      // checa unicidade (case-insensitive)
      const { data: existente, error: errSel } = await supabase
        .from("corretores")
        .select("id, tag_unica")
        .ilike("tag_unica", novo)
        .neq("id", meu._uuid);
      if (errSel) throw errSel;
      if (existente && existente.length > 0) {
        setTagMsg({ tipo: "err", texto: "Esse sufixo já está em uso. Escolha outro." });
        setTagSalvando(false);
        return;
      }

      const { error } = await supabase.from("corretores").update({ tag_unica: novo }).eq("id", meu._uuid);
      if (error) throw error;
      meu.tagUnica = novo;
      setTagInput(novo);
      setTagMsg({ tipo: "ok", texto: "✅ Link de indicação atualizado!" });
    } catch (e) {
      setTagMsg({ tipo: "err", texto: "Erro: " + e.message });
    } finally {
      setTagSalvando(false);
    }
  }

  async function uploadFoto(file) {
    if (!isSupabaseReady || !meu?._uuid) return;
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setFotoMsg({ tipo: "err", texto: "Máx 5 MB." }); return; }
    if (!/image\//i.test(file.type)) { setFotoMsg({ tipo: "err", texto: "Use uma imagem (JPG/PNG/WebP)." }); return; }

    setFotoUp(true);
    setFotoMsg(null);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `corretores/${meu._uuid}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("avatars").upload(path, file, {
        contentType: file.type, upsert: true,
      });
      if (upErr) throw upErr;

      const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
      const url = pub.publicUrl;

      const { error: updErr } = await supabase.from("corretores").update({ foto_url: url }).eq("id", meu._uuid);
      if (updErr) throw updErr;

      meu.foto = url;
      meu.foto_url = url;
      setFoto(url);
      setFotoMsg({ tipo: "ok", texto: "✅ Foto atualizada!" });
    } catch (e) {
      setFotoMsg({ tipo: "err", texto: "Erro: " + e.message });
    } finally {
      setFotoUp(false);
    }
  }

  const copiarLink = () => {
    navigator.clipboard.writeText(linkFinal)
      .then(() => alert("Link copiado!"))
      .catch(() => prompt("Copie:", linkFinal));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <SectionHeader icon="👤" title="Meu Perfil" />

      {/* Card principal — foto editável */}
      <Card style={{ textAlign: "center", padding: 24, background: ac.bg || T.cardSoft, border: `1px solid ${ac.border || T.border}` }}>
        <div style={{ display: "inline-block", position: "relative" }}>
          <Avatar initials={initials} size={84} photo={foto} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={fotoUp}
            title="Trocar foto"
            style={{
              position: "absolute", bottom: -2, right: -2,
              width: 28, height: 28, borderRadius: "50%",
              background: T.brand, color: T.textInv,
              border: `2px solid ${T.surface}`, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 14, fontFamily: "inherit",
            }}
          >{fotoUp ? "…" : "📷"}</button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={e => uploadFoto(e.target.files?.[0])}
          />
        </div>
        <div className="serif" style={{ fontSize: 22, fontWeight: 600, color: T.text, marginTop: 12 }}>{meu.nome}</div>
        <div style={{ fontSize: 12, color: T.textSub, marginTop: 4 }}>
          📍 {meu.cidade}-{meu.uf}
          {timeDoc && <span> · {timeDoc.emoji} {timeDoc.nome}</span>}
        </div>
        <div style={{ marginTop: 10 }}>
          <PilulaAwardTime awardId={aw.id} timeEmoji={timeDoc?.emoji} />
        </div>
        {fotoMsg && (
          <div style={{
            marginTop: 10, fontSize: 11,
            color: fotoMsg.tipo === "ok" ? T.ok : T.err,
          }}>{fotoMsg.texto}</div>
        )}
      </Card>

      {/* Progresso */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
          📈 Progresso
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12, marginBottom: 16 }}>
          <Stat label="Pontos totais" valor={p.total} />
          <Stat label="Vendas diretas" valor={meu.vendasDiretas || 0} />
          <Stat label="Volume total" valor={fmt.brl(meu.vendasValor || 0)} isText />
          <Stat label="Volume mês" valor={fmt.brl(meu.vendasValorMes || 0)} isText />
          <Stat label="Volume semana" valor={fmt.brl(meu.vendasValorSemana || 0)} isText />
          <Stat label="Rede ativa" valor={`${meu.ativados || 0} ativados`} isText />
        </div>
        {nextAw && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.textSub, marginBottom: 4 }}>
              <span>{aw.nome}</span>
              <span>{nextAw.nome} ({nextAw.pts} pts)</span>
            </div>
            <Progress value={p.total} max={nextAw.pts} color={ac.fg || T.brand} height={6} />
            <div style={{ textAlign: "center", marginTop: 6, fontSize: 11, color: T.textDim }}>
              {progresso}% — faltam {nextAw.pts - p.total} pontos
            </div>
          </div>
        )}
        {!nextAw && (
          <div style={{ textAlign: "center", fontSize: 12, color: T.gold }}>
            🏆 Nível máximo atingido! Diamond Award
          </div>
        )}
      </Card>

      {/* Link de indicação — mostra completo + copiar (edição vai no Dados Pessoais) */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
          🔗 Seu Link de Indicação
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <a
            href={linkFinal}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flex: 1, fontSize: 12, color: "#87CEEB", background: T.bg,
              padding: "8px 12px", borderRadius: T.r.md, border: `1px solid #87CEEB44`,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              textDecoration: "underline", fontFamily: "monospace",
            }}
            title="Abrir link em nova aba"
          >{linkFinal}</a>
          <button onClick={copiarLink} style={{
            padding: "8px 16px", borderRadius: T.r.md,
            background: T.brand + "22", border: `1px solid ${T.brand}44`,
            color: T.brand, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
          }}>📋 Copiar</button>
        </div>
        <div style={{ marginTop: 8, fontSize: 10, color: T.textDim }}>
          Edite o sufixo (@username) em <strong>Dados Pessoais</strong> abaixo. Cada ativação = 10 pontos!
        </div>
      </Card>

      {/* Dados cadastrais (read-only) */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
          📋 Dados Cadastrais
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Info label="Nome" valor={meu.nome} />
          <Info label="E-mail" valor={meu.email || "Não informado"} />
          <Info label="Telefone" valor={meu.telefone || "Não informado"} />
          <Info label="Cidade/UF" valor={`${meu.cidade}-${meu.uf}`} />
          <Info label="Nome de usuário (@)" valor={meu.tagUnica ? `@${meu.tagUnica}` : "—"} />
        </div>
        <div style={{ marginTop: 10, fontSize: 10, color: T.textDim }}>
          Para alterar e-mail, telefone ou cidade, vá em <strong>Conta &amp; Pagamento</strong>.
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, valor, isText }) {
  return (
    <div style={{ background: T.cardSoft, border: `1px solid ${T.border}`, borderRadius: T.r.md, padding: "10px 12px" }}>
      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <div className="serif" style={{ fontSize: isText ? 14 : 20, fontWeight: 600, color: T.text }}>{valor}</div>
    </div>
  );
}

function Info({ label, valor }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 13, color: T.text, marginTop: 2 }}>{valor}</div>
    </div>
  );
}
